import { action, computed, observable, toJS, runInAction } from 'mobx';
import { isEmpty, find, isNil, isArray } from 'lodash-es';
import { RootStore } from './';
import { bookingsApi, CreateBookingPassenger } from 'AppClient/api/Bookings';
import { removeEmptyProperties, isLoading } from 'utils/utils';
import { AlternativeContact, JourneyNotes, ReferenceField } from 'app-types';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { BookingPassengerInfo } from 'AppClient/api/UserProfile/UserProfileTypes'
import { UserInfo } from 'AppClient/api/UserProfile';
import { CONTACT_DETAILS } from 'AppClient/components/PricingBooking/PricingBooking'

export interface AmendingDetails extends JourneyNotes {
	firstName: string;
	lastName: string;
	emailAddress: string;
	phoneNumber: string;
	phoneCodeCountryId: number;
	phoneCode: string;
	referenceFields?: ReferenceField[];
	alternativeContactList: AlternativeContact[];
}

export interface UserInfoExtended extends Omit<UserInfo, 'referenceFields'> {
	referenceFields: ReferenceField[];
}

export class PassengerDetailsStore {
	@observable private _isAmendingDetailsFormValid: boolean = false;
	@observable private _amendingDetails: AmendingDetails | null = null;
	@observable private _amendedPassengerDetails: BookingPassengerInfo | null = null;
	@observable private fetchStatus: FetchStatus = FetchStatus.IDLE;
	@observable private _journeyNotes: string | null = null;
	@observable isFieldsTouched: boolean = false;
	@observable openDetailsKey: string = '';

	constructor(private rootStore: RootStore) { }

	@computed
	get amendedPassengerDetails() {
		return toJS(this._amendedPassengerDetails);
	}

	@computed
	get amendingDetails() {
		return toJS(this._amendingDetails);
	}

	@computed
	get journeyNotes() {
		return this._journeyNotes;
	}

	@computed
	get isAmendingDetailsFormValid() {
		return toJS(this._isAmendingDetailsFormValid);
	}

	@computed
	get getOpenDetailsKey() {
		if (!this.isFieldsTouched) {
			if (this.isAmendingDetailsFormValid) {
				return this.openDetailsKey;
			}
			else {
				return CONTACT_DETAILS
			}
		}

		return null;
	}

	@computed
	get userInfo() {
		const { userInfo: defaultUserInfo, customerInfoData } = this.rootStore.userStore!;
		const { amendedPassengerDetails, amendingDetails } = this;
		let referenceFields = [] as ReferenceField[];
		const initJourneyNotes = defaultUserInfo && defaultUserInfo.journeyNotes;

		if (!isNil(amendingDetails) && !isEmpty(amendingDetails)) {
			return {
				...amendingDetails,
				...(this.getJourneyNotes(initJourneyNotes))
			} as AmendingDetails;
		}
		if (!isNil(amendedPassengerDetails) && !isEmpty(amendedPassengerDetails)) {
			return {
				...amendedPassengerDetails,
				...(this.getJourneyNotes())
			} as BookingPassengerInfo;
		}

		if (!isNil(customerInfoData) && !isNil(defaultUserInfo)) {
			referenceFields = customerInfoData.referenceFields.map(item => {
				const nameData = find(
					defaultUserInfo.referenceFields,
					name => name.customerReferenceFieldId === item.id
				);

				return {
					customerReferenceFieldId: Number(item.id),
					referenceFieldName: item.name,
					value: (nameData && nameData.defaultValue) || '',
				};
			});
		}

		return {
			...defaultUserInfo,
			...(this.getJourneyNotes(initJourneyNotes)),
			referenceFields
		} as UserInfoExtended;
	}

	getJourneyNotes(initValue?: string | null) {
		if (isNil(this.journeyNotes) && initValue) {
			return {
				journeyNotes: initValue
			}
		}

		return {
			journeyNotes: this.journeyNotes
		}
	}

	@computed
	get isLoadingPassengerDetails() {
		return isLoading(this.fetchStatus);
	}

	@action.bound
	clearStore() {
		this._isAmendingDetailsFormValid = false;
		this._amendingDetails = null;
		this._journeyNotes = null;
		this._amendedPassengerDetails = null;
		this.openDetailsKey = '';
		this.isFieldsTouched = false;
	}

	@action.bound
	updateAmendingDetailsAndValidator(data: AmendingDetails, isValid: boolean, isFieldsTouched: boolean) {
		this._amendingDetails = data;
		this._isAmendingDetailsFormValid = isValid;
		this.isFieldsTouched = isFieldsTouched;
	}

	@action.bound
	updateJourneyNotes(data: string) {
		this._journeyNotes = data;
	}

	@action.bound
	changeOpenDetailsKey(key: string[] | string) {
		let newKey;

		if (isArray(key)) {
			newKey = key[0];
		} else {
			newKey = key;
		}

		this.openDetailsKey = newKey;
	}

	@action.bound
	async fetchPassengersDetails(previousBookingId: number) {
		if (isEmpty(this.amendingDetails)) {
			this.fetchStatus = FetchStatus.PENDING;
			try {
				const response = await bookingsApi.getBookingPassenger({
					bookingId: previousBookingId,
				});
				const {
					data: { error, result },
				} = response;
				if (!error) {
					runInAction(() => {
						this.fetchStatus = FetchStatus.SUCCESS;
						this._amendedPassengerDetails = result;
						if (this._amendedPassengerDetails && this._amendedPassengerDetails.journeyNotes) {
							this.updateJourneyNotes(this._amendedPassengerDetails.journeyNotes)
						}
					});
				}
			} catch (e) {
				this.fetchStatus = FetchStatus.FAILURE;
				this.rootStore.alertStore.setRequestError(e);
			}
		}
	}

	createPassengerBooking = () => {
		return bookingsApi.createBookingPassenger(this.getNewPassengerData());
	};

	getNewPassengerData = (): CreateBookingPassenger => {
		const userId = this.rootStore.userStore.user.profile.sub;
		const bookingId = this.rootStore.newBookingsStore.bookBookingData.bookingId!;
		const details = this.userInfo || ({} as AmendingDetails);

		return removeEmptyProperties({
			...details,
			userId,
			bookingId,
		});
	};
}
